import { ComponentRef, Injectable, OnDestroy } from '@angular/core';
import {
  isObservable,
  Observable,
  ReplaySubject,
  Subject,
  take,
  takeUntil,
} from 'rxjs';

import { getValueFromSourceWithPropertySubPathIfNeeded } from '../utils/resolve-values';

type Topic = {
  id: string;
  property: string;
  subject: Subject<unknown>;
  subscribers: ComponentRef<unknown>[];
};

/**
 * Think of IdProviderService as a service bus. In this service bus topics can be created and subscribed to.
 * Topics are identified by an `id` and a `property`.
 * Topics are used keep track of which subscribers should get notified by a publisher.
 * A subscriber in case of the dynamic framework is likely a component.
 * And the publisher is a component with an id, that publishes a value on a topic.
 * It can be so that a component subscribes to a topic (which gets created if it does not exist yet)
 * before a component with this 'id' + 'property' exists to publish a value on that topic.
 * In this case the component will get a resolved value from the publishing component once it is reported to exists.
 *
 * NOTE:
 * - Resolving values from other components by using their IDs requires that all IDs used
 *   with a single instance of this service to be unique. This is called a scope.
 *   For getting an instance for a scope, see the IdProviderServiceFactoryService.
 * - A component get ask for a value from 'my-id' + 'my-property', but if there will never be a component created with 'my-id' then
 *   a value will never be resolved, without a warning and without an error.
 *   This is due to the nature of the dynamic framework, where components are created and destroyed dynamically.
 */
@Injectable()
export class IdProviderService implements OnDestroy {
  private readonly idToComponentRefMap: Map<string, ComponentRef<unknown>> =
    new Map();
  private readonly idPropertyToTopicMap = new Map<string, Map<string, Topic>>();

  private readonly subscribersToTopicsMap = new Map<
    ComponentRef<unknown>,
    Topic[]
  >();

  private readonly componentUnregisteredByIdInternal$ = new Subject<{
    componentRef: ComponentRef<unknown>;
    id: string;
  }>();

  private readonly componentsSetupUp: string[] = [];

  public readonly componentUnregisteredById$ =
    this.componentUnregisteredByIdInternal$.asObservable();

  public readonly postComponentsHaveBeenSetupActions: (() => void)[] = [];

  private rootWasSetup = false;

  constructor() {
    this.componentUnregisteredById$.subscribe(({ componentRef }) => {
      this.unsubscribeFromTopics(componentRef);
    });
  }

  public ngOnDestroy(): void {
    this.clear();
  }

  public instantiate(isRoot: boolean): void {
    if (isRoot) {
      if (this.rootWasSetup) {
        throw new Error('Should only instantiate once for a root.');
      }
      this.rootWasSetup = true;
    }

    if (!this.rootWasSetup) {
      return;
    }

    for (const action of this.postComponentsHaveBeenSetupActions) {
      action();
    }
    this.postComponentsHaveBeenSetupActions.length = 0;
  }

  public registerComponent(
    componentRef: ComponentRef<unknown>,
    id: string,
  ): void {
    if (this.idToComponentRefMap.has(id)) {
      throw new Error(`Component with id ${id} already registered.`);
    }

    this.idToComponentRefMap.set(id, componentRef);

    componentRef.onDestroy(() => {
      this.idToComponentRefMap.delete(id);
      this.componentUnregisteredByIdInternal$.next({ componentRef, id });
    });
  }

  public getComponentRef(id: string): ComponentRef<unknown> | undefined {
    const componentRef = this.idToComponentRefMap.get(id);
    return componentRef;
  }

  public componentHasBeenSetup(
    componentRef: ComponentRef<unknown>,
    id: string,
  ): void {
    this.componentsSetupUp.push(id);
    this.notifySubscribers(componentRef, id);
  }

  public resolveValue(
    subscriber: ComponentRef<unknown>,
    id: string,
    property: string,
  ): Observable<unknown> {
    const { topic, isNew } = this.getOrCreateTopic(id, property);
    this.ensureIsSubscribedToTopic(topic, subscriber);
    this.ensureTopicIsRegisteredOnSubscriber(subscriber, topic);
    if (isNew) {
      this.setupValueEmissionFromSourceIfSetup(id, property, topic.subject);
    }

    const observable$ = topic.subject.asObservable();

    return observable$;
  }

  private getOrCreateTopic(
    id: string,
    property: string,
  ): { topic: Topic; isNew: boolean } {
    let propertyToTopicMap = this.idPropertyToTopicMap.get(id);
    if (propertyToTopicMap === undefined) {
      propertyToTopicMap = new Map<string, Topic>();
      this.idPropertyToTopicMap.set(id, propertyToTopicMap);
    }

    let topic = propertyToTopicMap.get(property);
    const isNew = topic === undefined;
    if (topic === undefined) {
      topic = {
        subject: new ReplaySubject(1),
        subscribers: [],
        id,
        property,
      };

      propertyToTopicMap.set(property, topic);
    }

    return { topic, isNew };
  }

  private ensureIsSubscribedToTopic(
    topic: Topic,
    subscriber: ComponentRef<unknown>,
  ) {
    if (!topic.subscribers.includes(subscriber)) {
      topic.subscribers.push(subscriber);
    }
  }

  private ensureTopicIsRegisteredOnSubscriber(
    subscriber: ComponentRef<unknown>,
    topic: Topic,
  ) {
    let topics = this.subscribersToTopicsMap.get(subscriber);
    if (topics === undefined) {
      topics = [];
      this.subscribersToTopicsMap.set(subscriber, topics);
    }
    topics.push(topic);
  }

  private notifySubscribers(
    componentRef: ComponentRef<unknown>,
    id: string,
  ): void {
    const propertyToTopicMap = this.idPropertyToTopicMap.get(id);
    if (propertyToTopicMap === undefined) {
      return;
    }

    for (const [property, topic] of propertyToTopicMap.entries()) {
      this.setupValueEmissionFromSource(
        id,
        property,
        topic.subject,
        componentRef,
      );
    }
  }

  private unsubscribeFromTopics(componentRef: ComponentRef<unknown>): void {
    const topics = this.subscribersToTopicsMap.get(componentRef);
    if (topics !== undefined) {
      for (const topic of topics) {
        const subscriberIndex = topic.subscribers.indexOf(componentRef);
        if (subscriberIndex !== -1) {
          topic.subscribers.splice(subscriberIndex, 1);

          this.completeAndRemoveTopicIfThereAreNoSubscribers(topic);
        }
      }

      this.subscribersToTopicsMap.delete(componentRef);
    }
  }

  private completeAndRemoveTopicIfThereAreNoSubscribers(topic: Topic) {
    if (topic.subscribers.length === 0) {
      topic.subject.complete();
      const propertyToTopicMap = this.idPropertyToTopicMap.get(topic.id);
      if (propertyToTopicMap !== undefined) {
        propertyToTopicMap.delete(topic.property);

        if (propertyToTopicMap.size === 0) {
          this.idPropertyToTopicMap.delete(topic.id);
        }
      }
    }
  }

  private setupValueEmissionFromSource(
    id: string,
    property: string,
    subject: Subject<unknown>,
    sourceComponentRef: ComponentRef<unknown>,
  ): void {
    this.setupValueEmissionFromSourceIfSetup(
      id,
      property,
      subject,
      sourceComponentRef,
    );
  }

  private setupValueEmissionFromSourceIfSetup(
    id: string,
    property: string,
    subject: Subject<unknown>,
    sourceComponentRef?: ComponentRef<unknown>,
  ): void {
    if (!this.componentsSetupUp.includes(id)) {
      // It could be so that it is already registered, but it should wait until it is setup.
      return;
    }

    if (sourceComponentRef === undefined) {
      sourceComponentRef = this.getComponentRef(id);

      if (sourceComponentRef === undefined) {
        return;
      }
    }

    const resolvedValue = this.resolveValueFromSource(
      sourceComponentRef,
      property,
    );

    this.emitValueUntilDestroyed(
      sourceComponentRef,
      id,
      resolvedValue,
      subject,
    );
  }

  private resolveValueFromSource(
    sourceComponentRef: ComponentRef<unknown>,
    property: string,
  ) {
    const resolvedValue = getValueFromSourceWithPropertySubPathIfNeeded(
      sourceComponentRef,
      sourceComponentRef,
      { property },
    );

    return resolvedValue;
  }

  private emitValueUntilDestroyed(
    sourceComponentRef: ComponentRef<unknown>,
    id: string,
    resolvedValue: unknown,
    subject: Subject<unknown>,
  ) {
    const isDestroyed$ = new Subject<void>();

    let failedToReactoToOnDestroy = false;
    try {
      sourceComponentRef.onDestroy(() => {
        const index = this.componentsSetupUp.indexOf(id);
        if (index !== -1) {
          this.componentsSetupUp.splice(index, 1);
        }
        isDestroyed$.next();
      });
    } catch (error) {
      console.warn(
        'IdProviderService tried to unsubscribe upon onDestroy of the ComponentRef, however, an error occurred. Therefore it is falling back to take(1) instead.',
        error,
      );
      failedToReactoToOnDestroy = true;
      isDestroyed$.complete();
    }

    if (isObservable(resolvedValue)) {
      let obs$ = resolvedValue;
      if (!failedToReactoToOnDestroy) {
        obs$ = obs$.pipe(takeUntil(isDestroyed$));
      } else {
        // Fallback to take(1)
        obs$ = obs$.pipe(take(1));
      }
      obs$.subscribe({
        next: (value) => {
          subject.next(value);
        },
        complete: () => {
          isDestroyed$.complete();
        },
      });
    } else {
      subject.next(resolvedValue);
    }
  }

  private clear(): void {
    if (this.idPropertyToTopicMap !== undefined) {
      const idPropertyToTopicMapEntries = this.idPropertyToTopicMap.entries();
      for (const [id, propertyToTopicMap] of idPropertyToTopicMapEntries) {
        const propertyToTopicMapEntries = propertyToTopicMap.entries();
        for (const [property, topic] of propertyToTopicMapEntries) {
          topic.subject.complete();
          propertyToTopicMap.delete(property);
        }
        this.idPropertyToTopicMap.delete(id);
      }

      this.componentsSetupUp.length = 0;
      this.idPropertyToTopicMap.clear();
    }
  }
}
